import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { LoginResponse } from '../api/auth'
import {
  PAYMENT_KIND_LABELS, practicePaymentsApi, type PaymentKind, type PaymentQueue, type QueueFilter, type QueueRow, type SuggestedContract,
} from '../api/practicePayments'
import { dateIt, errMsg, eur } from '../lib/financeFormat'
import {
  Button, Callout, DataTable, DataTableEmptyRow, Dialog, EmptyState, LoadingState, Page, PageHeader, Pagination, StatusPill, Tabs,
  TextField, useConfirm, useSnackbar, type PillTone,
} from '../components/ui'

/**
 * IMP-3 — coda dei movimenti della mandante senza pratica: "da verificare" (abbinamento debole per solo cognome + operatore:
 * la pratica suggerita si conferma con un click) e "senza pratica" (nessuna ipotesi: si abbina a mano). Confermare o abbinare
 * porta la pratica a Attivata/Stornata e ne applica il prodotto come l'import; ogni abbinamento e' in audit. Un movimento gia'
 * abbinato non si sposta. Il gating reale e' server-side (lettura: FinanceAdministrationRead; abbinamento: FinanceAdministration).
 */

const KIND_TONE: Record<PaymentKind, PillTone> = { Pagamento: 'success', Storno: 'error', Ricorrente: 'info' }
const PAGE_SIZE = 50

function monthIt(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
}

export function PracticePaymentsQueuePage({ auth }: { auth: LoginResponse }) {
  const writer = auth.role === 'Administrator' || auth.role === 'EqualAdmin' || auth.role === 'EqualAmministrazione'
  const [filter, setFilter] = useState<QueueFilter>('da_verificare')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<PaymentQueue | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<number | null>(null)
  const snackbar = useSnackbar()
  const confirm = useConfirm()
  const [pickRow, setPickRow] = useState<QueueRow | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await practicePaymentsApi.queue(filter, page, PAGE_SIZE))
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare la coda.'))
    } finally {
      setLoading(false)
    }
  }, [filter, page])

  useEffect(() => { void load() }, [load])

  async function assign(row: QueueRow, contractId?: number) {
    setBusy(row.id)
    try {
      const r = await practicePaymentsApi.assign(row.id, contractId)
      const state = r.statusTo ? ` Pratica portata a ${r.statusTo}.` : ''
      snackbar.show({ message: `Movimento abbinato alla pratica ${r.contractId}.${state}`, tone: 'success' })
      await load()
    } catch (e) {
      snackbar.show({ message: errMsg(e, 'Abbinamento non riuscito.'), tone: 'error' })
    } finally {
      setBusy(null)
    }
  }

  async function confirmSuggestion(row: QueueRow) {
    const s = row.suggestedContract
    if (!s) return
    const ok = await confirm({
      title: 'Conferma l’abbinamento',
      message: `Abbinare il movimento alla pratica ${s.referenceCode}${s.customerName ? ` (${s.customerName})` : ''}? La pratica passerà allo stato indicato dal movimento e riceverà il prodotto del report.`,
      confirmLabel: 'Conferma',
    })
    if (ok) await assign(row)
  }

  async function pickContract(c: SuggestedContract) {
    const row = pickRow
    setPickRow(null)
    if (row) await assign(row, c.contractId)
  }

  const counts = data?.counts
  const rows = data?.items ?? []
  const showAmount = data?.canSeeAmounts === true

  return (
    <Page>
      <PageHeader title="Movimenti mandante da abbinare" icon="rule" />
      {!writer && <Callout tone="info">Sola lettura: l’abbinamento richiede il ruolo Amministrazione.</Callout>}
      {error && <Callout tone="error">{error}</Callout>}
      <Callout tone="info">
        Riconosciuto dalla mandante, non incasso. I movimenti qui sotto non sono ancora collegati a una pratica e non cambiano stato né prodotto finché non li abbini.
      </Callout>
      <Tabs
        aria-label="Code dei movimenti"
        value={filter}
        onChange={id => { setFilter(id as QueueFilter); setPage(1) }}
        items={[
          { id: 'da_verificare', label: `Da verificare${counts ? ` (${counts.daVerificare})` : ''}` },
          { id: 'senza_pratica', label: `Senza pratica${counts ? ` (${counts.senzaPratica})` : ''}` },
          { id: 'tutti', label: 'Tutti' },
        ]}
      />
      {loading && !data ? <LoadingState /> : data && (
        data.total === 0 ? (
          <EmptyState icon="task_alt" title="Coda vuota" description="Nessun movimento da abbinare in questa vista." />
        ) : (
          <DataTable
            aria-label="Movimenti da abbinare"
            footer={<Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onChange={setPage} />}
          >
            <thead>
              <tr>
                <th>Report</th><th>Tipo</th><th>Cliente nel report</th><th>Prodotto</th>
                {showAmount && <th className="text-end">Importo</th>}
                <th>Pratica suggerita</th>{writer && <th><span className="md-sr-only">Azioni</span></th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <DataTableEmptyRow colSpan={showAmount ? 7 : 6} title="Nessun movimento" description="" />}
              {rows.map(r => (
                <tr key={r.id}>
                  <td>{monthIt(r.periodMonth)}{r.sourceRef && <div className="text-muted md-typescale-body-small">chiave {r.sourceRef}</div>}</td>
                  <td><StatusPill tone={KIND_TONE[r.kind]}>{PAYMENT_KIND_LABELS[r.kind]}</StatusPill></td>
                  <td>{r.customerLabel ?? '—'}{r.operatorName && <div className="text-muted md-typescale-body-small">{r.operatorName}</div>}</td>
                  <td>{r.productName ?? '—'}</td>
                  {showAmount && <td className="text-end md-num">{eur(r.amount)}</td>}
                  <td>
                    {r.suggestedContract ? (
                      <>
                        <Link to={`/contracts/${r.suggestedContract.contractId}`}>{r.suggestedContract.referenceCode}</Link>
                        <div className="text-muted md-typescale-body-small">
                          {[r.suggestedContract.customerName, r.suggestedContract.operatorName, r.suggestedContract.status,
                            r.suggestedContract.signedAt ? `firmata ${dateIt(r.suggestedContract.signedAt)}` : null].filter(Boolean).join(' · ')}
                        </div>
                        <div className="text-muted md-typescale-body-small">Solo cognome + operatore: da verificare</div>
                      </>
                    ) : <span className="text-muted">{r.note ?? 'Nessuna ipotesi'}</span>}
                  </td>
                  {writer && (
                    <td className="text-nowrap text-end">
                      {r.suggestedContract && (
                        <Button variant="filled" size="sm" disabled={busy === r.id} onClick={() => void confirmSuggestion(r)}>Conferma</Button>
                      )}{' '}
                      <Button size="sm" variant="outlined" disabled={busy === r.id} onClick={() => setPickRow(r)}>
                        {r.suggestedContract ? 'Altra pratica' : 'Abbina'}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
        )
      )}
      <ContractPickerDialog row={pickRow} onClose={() => setPickRow(null)} onPick={c => void pickContract(c)} />
    </Page>
  )
}

/** C-17: ricerca della pratica per codice o per cliente (al posto del numero interno). */
function ContractPickerDialog({ row, onClose, onPick }: { row: QueueRow | null; onClose: () => void; onPick: (c: SuggestedContract) => void }) {
  const [q, setQ] = useState('')
  const [items, setItems] = useState<SuggestedContract[]>([])
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState('')
  const seq = useRef(0)

  useEffect(() => { if (row) { setQ(row.customerLabel ?? ''); setItems([]); setError('') } }, [row])

  useEffect(() => {
    if (!row) return
    const text = q.trim()
    if (text.length < 2) { setItems([]); return }
    const mine = ++seq.current
    const t = setTimeout(async () => {
      setSearching(true)
      setError('')
      try {
        const r = await practicePaymentsApi.searchContracts(text)
        if (mine === seq.current) setItems(r.items)
      } catch (e) {
        if (mine === seq.current) setError(errMsg(e, 'Ricerca non riuscita.'))
      } finally {
        if (mine === seq.current) setSearching(false)
      }
    }, 300)
    return () => clearTimeout(t)
  }, [q, row])

  return (
    <Dialog
      open={row !== null}
      onClose={onClose}
      title="Abbina a una pratica"
      supportingText="Cerca per codice pratica o per nome del cliente, poi scegli la pratica."
      size="md"
      actions={<Button variant="text" onClick={onClose}>Annulla</Button>}
    >
      <TextField label="Codice pratica o cliente" value={q} onChange={e => setQ(e.target.value)} autoFocus />
      {error && <Callout tone="error">{error}</Callout>}
      {searching && <p className="text-muted md-typescale-body-small mt-2 mb-0">Ricerca…</p>}
      {!searching && q.trim().length >= 2 && items.length === 0 && !error && (
        <p className="text-muted md-typescale-body-small mt-2 mb-0">Nessuna pratica trovata.</p>
      )}
      <ul className="list-unstyled mt-2 mb-0">
        {items.map(c => (
          <li key={c.contractId} className="py-2 border-bottom">
            <div className="d-flex justify-content-between align-items-center gap-2">
              <div>
                <strong>{c.referenceCode}</strong>
                <div className="text-muted md-typescale-body-small">
                  {[c.customerName, c.operatorName, c.status, c.signedAt ? `firmata ${dateIt(c.signedAt)}` : null].filter(Boolean).join(' · ')}
                </div>
              </div>
              <Button size="sm" variant="filled" onClick={() => onPick(c)}>Abbina</Button>
            </div>
          </li>
        ))}
      </ul>
    </Dialog>
  )
}
